import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";

import { setTokenGetter, api } from "./lib/api";
import AppLayout from "./components/layout/AppLayout";
import Login from "./pages/auth/Login";
import Onboarding from "./pages/onboarding/Onboarding";
import Dashboard from "./pages/dashboard/Dashboard";
import BrandList from "./pages/brands/BrandList";
import BrandEditor from "./pages/brands/BrandEditor";
import CampaignList from "./pages/campaigns/CampaignList";
import CampaignEditor from "./pages/campaigns/CampaignEditor";
import ReviewQueue from "./pages/review/ReviewQueue";
import ContentCalendar from "./pages/calendar/ContentCalendar";
import Analytics from "./pages/analytics/Analytics";
import CampaignAnalytics from "./pages/analytics/CampaignAnalytics";
import Billing from "./pages/billing/Billing";
import CheckoutSuccess from "./pages/billing/CheckoutSuccess";
import Settings from "./pages/settings/Settings";
import SettingsSecurity from "./pages/settings/SettingsSecurity";
import SettingsConnectedPlatforms from "./pages/settings/SettingsConnectedPlatforms";
import SettingsPreferences from "./pages/settings/SettingsPreferences";
import SettingsTeam from "./pages/settings/SettingsTeam";
import SocialSetup from "./pages/setup/SocialSetup";
import AcceptInvite from "./pages/team/AcceptInvite";

type OnboardingState =
  | null // not yet loaded
  | "needs_onboarding" // brand + campaign not yet created → /onboarding
  | "needs_social_setup" // onboarding done, social not connected → /setup/social?brandId=
  | "needs_subscription" // onboarding done but no active paid plan → /billing
  | "complete"; // fully set up → Dashboard

// Routes that free/expired users may still access (billing + account management)
const SUBSCRIPTION_EXEMPT_PATHS = [
  "/billing",
  "/settings",
  "/onboarding",
  "/setup",
  "/invite",
];

function RequireSubscription({
  onboardingState,
}: {
  onboardingState: OnboardingState;
}) {
  const location = useLocation();
  const isExempt = SUBSCRIPTION_EXEMPT_PATHS.some((p) =>
    location.pathname.startsWith(p),
  );
  if (onboardingState === "needs_subscription" && !isExempt) {
    return <Navigate to="/billing" replace />;
  }
  return <AppLayout />;
}

export default function App() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [onboardingState, setOnboardingState] = useState<OnboardingState>(null);
  // The first brand's ID — needed to scope the social setup redirect
  const [firstBrandId, setFirstBrandId] = useState<string | null>(null);

  // Wire Clerk's getToken into the API client so every request gets a fresh JWT
  useEffect(() => {
    setTokenGetter(getToken);
  }, [getToken]);

  // After sign-in, determine where the user is in the onboarding sequence
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!isSignedIn) {
        setOnboardingState(null);
        setFirstBrandId(null);
        return;
      }

      try {
        const { profile } = await api.auth.me();

        if (!profile) {
          // Profile not yet provisioned — treat as needs onboarding
          if (!cancelled) setOnboardingState("needs_onboarding");
          return;
        }

        const p = profile as {
          onboardingCompleted?: boolean;
          socialConnectOnboardingCompleted?: boolean;
          plan?: string;
        };

        // Step 1: Has the user created their first brand + campaign?
        if (!p.onboardingCompleted) {
          if (!cancelled) setOnboardingState("needs_onboarding");
          return;
        }

        // Step 2: Have they connected social accounts (or intentionally skipped)?
        if (!p.socialConnectOnboardingCompleted) {
          // Fetch their first brand so we can scope the social setup correctly
          try {
            const { brands } = await api.brands.list();
            if (!cancelled) {
              setFirstBrandId(brands[0]?.brandId ?? null);
              setOnboardingState("needs_social_setup");
            }
          } catch {
            if (!cancelled) setOnboardingState("needs_social_setup");
          }
          return;
        }

        // Step 3: Do they have an active paid plan?
        // plan === "free" means either: brand-new user, trial expired, or cancelled.
        // In all cases we gate them to billing — there is no permanent free tier.
        if (!p.plan || p.plan === "free") {
          if (!cancelled) setOnboardingState("needs_subscription");
          return;
        }

        if (!cancelled) setOnboardingState("complete");
      } catch {
        if (!cancelled) setOnboardingState("needs_onboarding");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, user?.id]);

  if (!isLoaded) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Build the social setup URL with the first brand's ID (when known)
  const socialSetupUrl = firstBrandId
    ? `/setup/social?brandId=${firstBrandId}`
    : "/setup/social";

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route
          path="/login"
          element={isSignedIn ? <Navigate to="/" replace /> : <Login />}
        />
        {/* Invite accept — accessible regardless of auth/subscription state */}
        <Route path="/invite/:token" element={<AcceptInvite />} />

        {/* Protected */}
        <Route
          element={
            isSignedIn ? (
              <RequireSubscription onboardingState={onboardingState} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          {/* Root — redirect based on onboarding state */}
          <Route
            path="/"
            element={
              onboardingState === null ? (
                // Still loading profile — show spinner
                <div className="h-full flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : onboardingState === "needs_onboarding" ? (
                <Navigate to="/onboarding" replace />
              ) : onboardingState === "needs_social_setup" ? (
                <Navigate to={socialSetupUrl} replace />
              ) : onboardingState === "needs_subscription" ? (
                <Navigate to="/billing" replace />
              ) : (
                <Dashboard />
              )
            }
          />

          {/* Onboarding wizard — first brand + campaign creation */}
          <Route
            path="/onboarding"
            element={
              <Onboarding
                onComplete={(brandId) => {
                  setFirstBrandId(brandId);
                  setOnboardingState("needs_social_setup");
                }}
              />
            }
          />

          {/* Social setup wizard — scoped to a specific brand */}
          <Route
            path="/setup/social"
            element={
              <SocialSetup onComplete={() => setOnboardingState("complete")} />
            }
          />

          <Route path="/brands" element={<BrandList />} />
          <Route path="/brands/new" element={<BrandEditor />} />
          <Route path="/brands/:brandId/edit" element={<BrandEditor />} />
          <Route path="/brands/:brandId/campaigns" element={<CampaignList />} />
          <Route
            path="/brands/:brandId/campaigns/new"
            element={<CampaignEditor />}
          />
          <Route
            path="/brands/:brandId/campaigns/:campaignId/edit"
            element={<CampaignEditor />}
          />
          <Route path="/review" element={<ReviewQueue />} />
          <Route path="/calendar" element={<ContentCalendar />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route
            path="/analytics/campaigns/:campaignId"
            element={<CampaignAnalytics />}
          />
          <Route path="/billing" element={<Billing />} />
          <Route path="/billing/success" element={<CheckoutSuccess />} />
          <Route path="/settings" element={<Settings />}>
            <Route
              index
              element={<Navigate to="/settings/security" replace />}
            />
            <Route path="security" element={<SettingsSecurity />} />
            <Route path="platforms" element={<SettingsConnectedPlatforms />} />
            <Route path="preferences" element={<SettingsPreferences />} />
            <Route path="team" element={<SettingsTeam />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
